import { Dispatch, FC, SetStateAction, useCallback, useMemo } from 'react';
import { FATCATranslations } from '../../../utils/getFATCATranslations';
import { FATCA_PAGES_KEYS } from '../../../constants';
import { css } from '@bts-web/utils-style-engine';
import { NavigationBar, ProgressIndicatorWithSteps } from '../../../../common';
import { useFATCAFormState } from '../../../utils/useFATCAFormState';
import { FilledArrowLeft, FilledDismiss } from '../../../../common/svgIcons';

const buttonStyles = css({
  py: 'extra_small',
  borderRadius: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
});

const FATCAModalActionBar: FC<{
  translations: FATCATranslations;
  currentPage: FATCA_PAGES_KEYS;
  setPage: Dispatch<SetStateAction<FATCA_PAGES_KEYS>>;
  onClose?: () => void;
  stepInformation: ReturnType<typeof useFATCAFormState>['stepsInformation'];
}> = ({ currentPage, translations, setPage, onClose, stepInformation }) => {
  const actionBarTitle = useMemo(() => {
    switch (currentPage) {
      case FATCA_PAGES_KEYS.FATCA_USER_DATA:
        return translations.title_confirmDetails;

      case FATCA_PAGES_KEYS.FATCA_TAXATION_OPTIONS:
        return translations.title_confirmDetails;

      case FATCA_PAGES_KEYS.FATCA_TAX_RESIDENCY_PRIMARY_HUB:
        return translations.section_title_tax_residency;

      case FATCA_PAGES_KEYS.FATCA_FINAL_ALLOWED:
        return translations.title_confirmDetails;

      case FATCA_PAGES_KEYS.FATCA_FINAL_DISALLOWED:
        return translations.title_confirmDetails;

      case FATCA_PAGES_KEYS.FATCA_MISSING_TIN_REASON_PAGE:
        return translations.title_confirmDetails;

      case FATCA_PAGES_KEYS.FATCA_MISSING_TIN_TEXT_REASON_PAGE:
        return translations.title_confirmDetails;

      default:
        return '';
    }
  }, [currentPage, translations]);

  const backAction = useCallback(() => {
    let prevPageValue: FATCA_PAGES_KEYS;

    switch (currentPage) {
      case FATCA_PAGES_KEYS.FATCA_USER_DATA:
        if (onClose) {
          onClose();
        }

        return;

      case FATCA_PAGES_KEYS.FATCA_TAXATION_OPTIONS:
        prevPageValue = FATCA_PAGES_KEYS.FATCA_USER_DATA;

        break;

      case FATCA_PAGES_KEYS.FATCA_TAX_RESIDENCY_PRIMARY_HUB:
        prevPageValue = FATCA_PAGES_KEYS.FATCA_TAXATION_OPTIONS;

        break;

      case FATCA_PAGES_KEYS.FATCA_FINAL_ALLOWED:
        prevPageValue = FATCA_PAGES_KEYS.FATCA_TAXATION_OPTIONS;

        break;

      case FATCA_PAGES_KEYS.FATCA_FINAL_DISALLOWED:
        prevPageValue = FATCA_PAGES_KEYS.FATCA_TAXATION_OPTIONS;

        break;

      case FATCA_PAGES_KEYS.FATCA_MISSING_TIN_REASON_PAGE:
        prevPageValue = FATCA_PAGES_KEYS.FATCA_TAX_RESIDENCY_PRIMARY_HUB;

        break;

      case FATCA_PAGES_KEYS.FATCA_MISSING_TIN_TEXT_REASON_PAGE:
        prevPageValue = FATCA_PAGES_KEYS.FATCA_TAX_RESIDENCY_PRIMARY_HUB;

        break;

      default:
        prevPageValue = FATCA_PAGES_KEYS.FATCA_USER_DATA;
    }

    setPage(prevPageValue);
  }, [currentPage, setPage]);

  return (
    <>
      <NavigationBar
        className={css({
          backgroundColor: 'transparent',
        })}
        showBorderBottom={false}
        title={actionBarTitle || ' '}
        NavigationLeftSlot={
          <button
            type="button"
            className={buttonStyles}
            aria-label="back to previous FATCA step button"
            onClick={backAction}
            style={{
              marginInlineEnd: 'auto',
            }}
          >
            <FilledArrowLeft size={24} color="primary" data-dir="rtl" />
          </button>
        }
        NavigationRightSlot={
          <button
            aria-label="close FATCA modal button"
            className={buttonStyles}
            disabled={false}
            onClick={onClose}
            style={{
              marginInlineStart: 'auto',
            }}
          >
            <FilledDismiss size={24} color="primary" />
          </button>
        }
      />
      <div className={css({ p: 'extra_small' })}>
        <ProgressIndicatorWithSteps
          currentStep={stepInformation.currentStep}
          totalSteps={stepInformation.totalSteps}
        />
      </div>
    </>
  );
};

export { FATCAModalActionBar };
